import { useContext, useEffect, useState } from 'react';
import axios from 'axios';

import { AuthContext, Role } from '../../../../../context/AuthProvider';
import { fullNameUsernameOrEmail } from '../../../../../helpers/username';
import { trackAction } from '../../../../../helpers/trackAction';

interface UseInteractiveQuizProps {
  content: { pk: string };
  studyGroup: { pk: string };
  currentSectionCounter: number;
  iFrame: any;
  isContentLoading: boolean;
}

const getQuizQuestions = (iFrameRef: any, currentSectionCounter: number) => {
  const iframeDoc = iFrameRef.current.contentWindow.document;
  const quizQuestions = iframeDoc.querySelectorAll(
    `.content_section:nth-of-type(${
      currentSectionCounter + 1
    }) .interactive-quiz`,
  );
  return quizQuestions as HTMLElement[];
};

const getStudents = (
  studyGroupId: string,
  setStudents: (students: any) => void,
) => {
  axios
    .get('/api/v1/users/students/', {
      params: {
        format: 'json',
        study_group: studyGroupId,
      },
      headers: {
        'X-CSRFToken': JSON.parse(
          document.getElementById('csrf_token')!.innerHTML,
        ),
      },
    })
    .then((response) => {
      setStudents(response.data.results);
    })
    .catch((e) => console.error(e));
};

const getCurrentQuestionId = (
  iFrameRef: any,
  currentSectionCounter: number,
  questionIndex: number,
) => {
  const quizQuestions = getQuizQuestions(iFrameRef, currentSectionCounter);
  if (!quizQuestions[questionIndex]) {
    return -1;
  }
  return quizQuestions[questionIndex]!.id.replace('cms-plugin-', '');
};

const useInteractiveQuiz = ({
  content,
  studyGroup,
  currentSectionCounter,
  iFrame,
  isContentLoading,
}: UseInteractiveQuizProps) => {
  const user = useContext(AuthContext);
  const [students, setStudents] = useState<any[]>([]);
  const [studentAnswers, setStudentAnswers] = useState<Record<string, any>>({});
  const [doneSections, setDoneSections] = useState<number[]>([]);

  const isTeacher = user?.roles?.includes(Role.teacher);

  useEffect(() => {
    if (isContentLoading) {
      return;
    }
    if (doneSections.includes(currentSectionCounter)) {
      return;
    }
    setDoneSections((d) => [...d, currentSectionCounter]);
    setTimeout(() => {
      if (!iFrame.current) {
        return;
      }
      quizInit();
    }, 1000);
  }, [currentSectionCounter, isContentLoading]);

  useEffect(() => {
    if (isTeacher && studyGroup) {
      getStudents(studyGroup.pk, setStudents);
    }
  }, [studyGroup]);

  useEffect(() => {
    if (students && studentAnswers) {
      displayStudentAnswers();
    }
  }, [students, studentAnswers]);

  const quizInit = () => {
    const quizQuestions = getQuizQuestions(iFrame, currentSectionCounter);
    for (let i = 0; i < quizQuestions.length; i++) {
      getOldAnswers(i);
    }
  };

  const saveAnswer = (
    userAnswer: {
      isUserCorrect: boolean;
      selectedAnswers: number[];
    },
    questionIndex: number,
  ) => {
    const currentQuestionId = getCurrentQuestionId(
      iFrame,
      currentSectionCounter,
      questionIndex,
    );
    trackAction({
      action_type: 'answered_quiz',
      action_object: {
        type: 'content',
        pk: content.pk,
      },
      study_group: { pk: studyGroup.pk },
      data: {
        questionId: currentQuestionId,
        answers: userAnswer.selectedAnswers.join
          ? userAnswer.selectedAnswers.join(',')
          : userAnswer.selectedAnswers,
      },
    });
    axios
      .post(
        '/api/v1/syllabus/interactive_quiz_answers/',
        {
          question_id: currentQuestionId,
          study_group_id: studyGroup.pk,
          answers: userAnswer.selectedAnswers.join
            ? userAnswer.selectedAnswers.join(',')
            : userAnswer.selectedAnswers,
        },
        {
          headers: {
            'X-CSRFToken': JSON.parse(
              document.getElementById('csrf_token')!.innerHTML,
            ),
          },
        },
      )
      .then((response) => {})
      .catch((error) => {
        console.error(error);
      });
  };

  const getOldAnswers = (questionIndex: number) => {
    const quizQuestions = getQuizQuestions(iFrame, currentSectionCounter);
    if (!quizQuestions[questionIndex]) {
      return;
    }
    const repeatCount = parseInt(
      quizQuestions[questionIndex]!.querySelector('#repeat_count')!
        .textContent!,
    );
    const currentQuestionId = getCurrentQuestionId(
      iFrame,
      currentSectionCounter,
      questionIndex,
    );

    if (isTeacher) {
      axios
        .get('/api/v1/syllabus/interactive_quiz_answers/', {
          params: {
            study_group_id: studyGroup.pk,
            question_id: currentQuestionId,
            get_study_group_data: true,
          },
          headers: {
            'X-CSRFToken': JSON.parse(
              document.getElementById('csrf_token')!.innerHTML,
            ),
          },
        })
        .then((response) => {
          setStudentAnswers((sa) => {
            sa[currentQuestionId] = JSON.parse(response.data);
            return { ...sa };
          });
        })
        .catch((e) => console.error(e));
    } else {
      axios
        .get('/api/v1/syllabus/interactive_quiz_answers/', {
          params: {
            study_group_id: studyGroup.pk,
            question_id: currentQuestionId,
          },
          headers: {
            'X-CSRFToken': JSON.parse(
              document.getElementById('csrf_token')!.innerHTML,
            ),
          },
        })
        .then((response) => {
          const oldAnswersData = JSON.parse(response.data);
          const submitBtn = quizQuestions[questionIndex]!.querySelector(
            '.submit-btn',
          ) as HTMLElement;
          const questionType = JSON.parse(
            quizQuestions[questionIndex]!.querySelector('#question_type')!
              .textContent!,
          );
          if (oldAnswersData.length >= repeatCount && repeatCount !== -1) {
            // Block answer
            const answerIndexes = oldAnswersData[
              oldAnswersData.length - 1
            ].fields.answers
              .split(',')
              .map((a: string) => parseInt(a));
            quizQuestions[questionIndex]!.querySelectorAll(
              '.answer-container',
            ).forEach((answerButton, answerButtonIndex) => {
              if (answerIndexes.includes(answerButtonIndex + 1)) {
                (answerButton as HTMLElement).click();
              }
            });
            submitBtn.click();
          } else {
            // Add event listener
            if (repeatCount !== -1 && oldAnswersData.length > 0) {
              quizQuestions[questionIndex]!.querySelector(
                '.description',
              )!.innerHTML = `Attempt ${
                oldAnswersData.length + 1
              } of ${repeatCount}, last answer was ${
                oldAnswersData[oldAnswersData.length - 1].fields.answers
              } and it was ${
                oldAnswersData[oldAnswersData.length - 1].fields.is_correct
                  ? '<span style="color: green;">correct</span>'
                  : '<span style="color: red;">incorrect</span>'
              } answer`;
            }
            submitBtn?.addEventListener('click', () => {
              setTimeout(() => {
                const userResult = JSON.parse(
                  quizQuestions[questionIndex]!.querySelector('.user-result')!
                    .textContent!,
                );
                saveAnswer(userResult, questionIndex);
              }, 100);
            });
          }

          if (questionType === 'FreeForm') {
            if (oldAnswersData.length > 0) {
              quizQuestions[questionIndex]!.querySelector('textarea')!.value =
                oldAnswersData[oldAnswersData.length - 1].fields.answers;
            }
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  const displayStudentAnswers = () => {
    const quizQuestions = getQuizQuestions(iFrame, currentSectionCounter);
    for (let i = 0; i < quizQuestions.length; i++) {
      const questionId = getCurrentQuestionId(iFrame, currentSectionCounter, i);
      if (studentAnswers[questionId]) {
        const quizQuestion = quizQuestions[i]!.querySelector(
          '#student-data',
        ) as HTMLElement;
        let answersContent = `<button class="link-btn" onClick="const el = document.querySelector('#student-answers-${questionId}'); 
        el.className.includes('display') ? el.className = 'student-answers' :
        el.className += ' display'">Show student answers</button>`;
        answersContent += `<div id="student-answers-${questionId}" class="student-answers">`;
        if (studentAnswers[questionId].length === 0) {
          answersContent += '<p>No student answers</p>';
        }
        studentAnswers[questionId].forEach(({ fields: studentAnswer }: any) => {
          const student = students.find(
            (s) => s.pk === studentAnswer.student_id,
          );
          if (!student) {
            return;
          }
          const answers = studentAnswer.answers.split
            ? studentAnswer.answers
                .split(',')
                .map((a: string) => a + '.')
                .join(', ')
            : studentAnswer.answers;
          answersContent += `<li class="answer ${
            studentAnswer.is_correct ? 'correct' : 'false'
          }">${new Date(
            studentAnswer.created_at,
          ).toLocaleDateString()} ${new Date(
            studentAnswer.created_at,
          ).toLocaleTimeString()} | ${fullNameUsernameOrEmail(
            student,
          )} | ${answers}`;
        });
        answersContent += '</div>';
        quizQuestion.innerHTML = answersContent;
      }
    }
  };
};

export default useInteractiveQuiz;
